import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import EmailSignup from "../components/EmailSignup";
import ArticleCard from "../components/ArticleCard";
import AmbientMotion from "../components/AmbientMotion";
import { useSiteContent, setSiteContentCache } from "../hooks/useSiteContent";
import { useMeta } from "../hooks/useMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Defaults are only fallback values; every photo/text below is admin-editable.
const HERO_IMAGE =
  "https://images.unsplash.com/photo-1757133706647-b0a6bd89288e?auto=format&fit=crop&w=1800&q=80";
const AUDIENCE_GRANDPARENTS_IMG =
  "https://images.unsplash.com/photo-1683544033990-44278648c3f3?auto=format&fit=crop&w=1400&q=80";
const AUDIENCE_PARENTS_IMG =
  "https://images.unsplash.com/photo-1731338493464-db479f155a3a?auto=format&fit=crop&w=1400&q=80";
const AUDIENCE_LITTLEONES_IMG =
  "https://images.unsplash.com/photo-1704241370920-e67ce744d8cd?auto=format&fit=crop&w=1400&q=80";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1600779438084-a87b966aab99?auto=format&fit=crop&w=1600&q=80";
const SIGNATURE_STILL_IMG =
  "https://images.unsplash.com/photo-1767976768748-6e0751b3d153?auto=format&fit=crop&w=1400&q=80";

const audiences = (c) => [
  {
    label: c.audience_1_label || "For Grandparents",
    title: c.audience_1_title || "Quiet ways to be remembered.",
    body:
      c.audience_1_body ||
      "Reflections on presence, rituals, and the patient art of being known by someone too young to remember.",
    img: c.audience_1_image || AUDIENCE_GRANDPARENTS_IMG,
    alt: "A grandfather laughing as he lifts his baby grandchild up to his face",
  },
  {
    label: c.audience_2_label || "For Parents",
    title: c.audience_2_title || "Inviting in, without inviting over.",
    body:
      c.audience_2_body ||
      "Gentle frameworks for letting grandparents in on your terms — without losing your autonomy or your peace.",
    img: c.audience_2_image || AUDIENCE_PARENTS_IMG,
    alt: "A grandmother cradling a baby in a sunlit home",
  },
  {
    label: c.audience_3_label || "For Little Ones",
    title: c.audience_3_title || "Familiarity, woven slowly.",
    body:
      c.audience_3_body ||
      "How small repeated moments — a song, a greeting, a postcard — become the quiet architecture of belonging.",
    img: c.audience_3_image || AUDIENCE_LITTLEONES_IMG,
    alt: "A small child reading on the floor with toys and books around them",
  },
];

export default function Home() {
  const [articles, setArticles] = useState([]);
  const c = useSiteContent();

  useMeta({
    title: null, // default site title
    description:
      c.hero_subtext ||
      "Thoughtful reflections and practical guidance for grandparents navigating love, distance, boundaries, and belonging.",
    image: c.hero_image,
  });

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then((r) => setArticles(r.data || []))
      .catch(() => setArticles([]));
    // Keep the hook cache fresh on each home visit so admin edits show up.
    axios
      .get(`${API}/site/content`)
      .then((r) => setSiteContentCache(r.data || {}))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <AmbientMotion />
      <Header />

      {/* ---------- HERO ---------- */}
      <section
        data-testid="hero-section"
        className="relative pt-36 sm:pt-40 lg:pt-44 pb-24 sm:pb-32 overflow-hidden grain"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6 relative z-10">
            <p
              data-testid="hero-overline"
              className="text-xs uppercase tracking-[0.3em] text-primary fade-up"
            >
              {c.hero_overline || "Gentle connection across generations"}
            </p>
            <h1
              data-testid="hero-headline"
              className="mt-7 font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-light leading-[1.02] tracking-tight text-foreground fade-up fade-up-delay-1"
            >
              {c.hero_headline_lead || "Connection begins"}{" "}
              <span className="italic text-primary">
                {c.hero_headline_emphasis || "long before"}
              </span>{" "}
              {c.hero_headline_trail || "memory."}
            </h1>
            <p
              data-testid="hero-subtext"
              className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl fade-up fade-up-delay-2"
            >
              {c.hero_subtext ||
                "Thoughtful reflections and practical guidance for grandparents navigating love, distance, boundaries, and belonging."}
            </p>
            <div className="mt-10 flex items-center gap-8 fade-up fade-up-delay-3">
              <a
                href="#journal"
                data-testid="hero-cta-journal"
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 transition-all duration-300"
              >
                {c.hero_cta || "Read the Journal"}
                <ArrowRight
                  size={16}
                  strokeWidth={1.5}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </a>
              <a
                href="#about"
                data-testid="hero-cta-about"
                className="text-sm tracking-wide text-foreground/70 hover:text-primary transition-colors duration-300 editorial-link"
              >
                {c.hero_cta_secondary || "Who this is for"}
              </a>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] sm:aspect-[5/6] rounded-[2rem] overflow-hidden fade-up fade-up-delay-2">
              <img
                src={c.hero_image || HERO_IMAGE}
                alt="An older woman holding a child's hand"
                className="w-full h-full object-cover ken-burns-slow"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/30 via-transparent to-transparent" />
              <div className="absolute inset-0 light-sweep pointer-events-none" />
            </div>
            <div className="hidden sm:block absolute -bottom-10 -left-10 max-w-[260px] bg-background/90 backdrop-blur-md border border-border/70 rounded-2xl p-6 fade-up fade-up-delay-3">
              <p className="text-xs uppercase tracking-[0.25em] text-primary">
                {c.hero_quote_overline || "A quiet truth"}
              </p>
              <p className="mt-3 font-serif text-lg leading-snug italic text-foreground/90">
                {c.hero_quote || '"They will not remember the day. They will remember the feeling."'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- FEATURE CARDS ---------- */}
      <section
        id="for-whom"
        data-testid="audience-section"
        className="bg-secondary/40 border-y border-border/50"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-primary">
              {c.audience_overline || "Who this is for"}
            </p>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-light leading-tight">
              {c.audience_heading || "Three quiet vantage points on the same warm room."}
            </h2>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-6 lg:gap-8">
            {audiences(c).map((a, i) => {
              const target =
                i === 0
                  ? "/journal?audience=grandparents"
                  : i === 1
                  ? "/journal?audience=parents"
                  : "/journal?audience=little-ones";
              return (
                <Link
                  key={a.label}
                  to={target}
                  data-testid={`audience-card-${i}`}
                  className="group bg-background rounded-3xl overflow-hidden border border-border/60 hover:border-primary/50 hover:shadow-[0_18px_40px_-30px_rgba(60,55,45,0.25)] transition-all duration-700 flex flex-col fade-up"
                  style={{ animationDelay: `${0.15 * i}s` }}
                >
                  <div className="aspect-[5/4] overflow-hidden">
                    <img
                      src={a.img}
                      alt={a.alt}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
                    />
                  </div>
                  <div className="p-8 sm:p-10 flex-1 flex flex-col">
                    <p className="text-xs uppercase tracking-[0.25em] text-primary">
                      {a.label}
                    </p>
                    <h3 className="mt-4 font-serif text-2xl sm:text-[1.75rem] font-light leading-tight group-hover:text-primary transition-colors duration-500">
                      {a.title}
                    </h3>
                    <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed flex-1">
                      {a.body}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-foreground/70 group-hover:text-primary transition-colors">
                      Read essays
                      <ArrowRight
                        size={14}
                        strokeWidth={1.5}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- SIGNATURE / MARQUEE ---------- */}
      <section
        data-testid="signature-section"
        className="relative py-28 sm:py-40 overflow-hidden"
      >
        {/* Subtle rising steam — like the breath of a quiet tea cup */}
        <div className="absolute left-[10%] bottom-12 w-px pointer-events-none">
          <span className="steam" style={{ animationDelay: "0s" }} />
          <span className="steam" style={{ animationDelay: "2.5s", left: "14px" }} />
          <span className="steam" style={{ animationDelay: "5s", left: "-8px" }} />
        </div>
        <div className="absolute right-[12%] bottom-16 w-px pointer-events-none">
          <span className="steam" style={{ animationDelay: "1.5s" }} />
          <span className="steam" style={{ animationDelay: "4s", left: "10px" }} />
        </div>
        <div className="relative">
          <div className="gentle-marquee select-none whitespace-nowrap font-serif italic text-7xl sm:text-8xl lg:text-[10rem] leading-none text-accent/70">
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i} className="px-12 flex items-center">
                {(c.signature_marquee || "Tiny rituals. · Lasting bonds.")
                  .split(/\s*·\s*/)
                  .map((part, idx, arr) => (
                    <React.Fragment key={idx}>
                      {part}&nbsp;
                      {idx < arr.length - 1 && (
                        <>
                          <span className="not-italic text-primary/80">·</span>
                          &nbsp;
                        </>
                      )}
                    </React.Fragment>
                  ))}
                <span className="not-italic text-primary/80">·</span>&nbsp;
              </span>
            ))}
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 mt-16 sm:mt-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="aspect-[4/5] rounded-[2rem] overflow-hidden">
              <img
                src={c.signature_image || SIGNATURE_STILL_IMG}
                alt="A quiet stack of handwritten envelopes — the texture of a long-tended ritual."
                loading="lazy"
                className="w-full h-full object-cover ken-burns-slow"
              />
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2 text-left">
            <p className="text-xs uppercase tracking-[0.3em] text-primary">
              {c.signature_overline || "Our signature philosophy"}
            </p>
            <p className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl leading-[1.3] font-light text-foreground">
              {c.signature_text ||
                "A voice on the phone at the same hour. A song before goodbye. A postcard with no occasion. A familiar greeting, repeated until it becomes a small piece of home."}
            </p>
            <p className="mt-6 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-xl">
              {c.signature_body ||
                "Young grandchildren remember in patterns long before they remember in stories. Repetition, gently held, is how love becomes memory."}
            </p>
          </div>
        </div>
      </section>

      {/* ---------- FEATURED ARTICLES ---------- */}
      <section
        id="journal"
        data-testid="articles-section"
        className="bg-secondary/30 border-y border-border/50"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.3em] text-primary">
                {c.journal_overline || "The Journal"}
              </p>
              <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-light leading-tight">
                {c.journal_heading || "Slow essays for tender questions."}
              </h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-sm md:text-right">
              {c.journal_description ||
                "Written for the quiet moments — when the call has ended, the house is still, and the heart has questions."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 lg:gap-x-16 lg:gap-y-20">
            {articles.map((a, i) => (
              <ArticleCard key={a.slug} article={a} index={i} />
            ))}
          </div>

          <div className="mt-16 sm:mt-20 text-center">
            <Link
              to="/journal"
              data-testid="view-all-essays"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-border text-sm tracking-wide text-foreground hover:bg-secondary transition-colors duration-300"
            >
              View all essays
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- ABOUT ---------- */}
      <section id="about" data-testid="about-section" className="bg-background">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden">
              <img
                src={c.about_image || ABOUT_IMG}
                alt="A vintage letter being written"
                className="w-full h-full object-cover ken-burns-slow"
              />
              <div className="absolute inset-0 light-sweep pointer-events-none opacity-70" />
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.3em] text-primary">
              About GentleGiTo
            </p>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              {c.about_heading || "A calm space for modern families."}
            </h2>
            <div className="mt-8 space-y-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              <p>
                {c.about_body_1 ||
                  "GentleGiTo is a calm space for modern families who want connection with respect, warmth, and wisdom."}
              </p>
              <p>
                {c.about_body_2 ||
                  "Our work sits at the intersection of developmental research, family psychology, and lived practice."}
              </p>
              <p className="italic font-serif text-xl text-foreground/80">
                {c.about_body_3 ||
                  "We believe connection is not a grand gesture. It is a quiet, repeated act of showing up."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- EMAIL SIGNUP ---------- */}
      <EmailSignup />

      <Footer />
    </div>
  );
}
